import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { customerService } from '../../services/customerService';
import { supabase } from '../../lib/supabase';
import RoleBasedNavigation from '../../components/ui/RoleBasedNavigation';
import AuthenticationPrompt from '../../components/ui/AuthenticationPrompt';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import OrderHistoryCard from '../customer-dashboard/components/OrderHistoryCard';
import RatingModal from '../customer-dashboard/components/RatingModal';

const MyOrders = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // all, pending, preparing, ready, delivered, cancelled
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    loadOrders();

    // Set up real-time subscription for order updates
    const setupRealtimeSubscription = async () => {
      try {
        // Get customer profile to filter orders
        const { data: customer } = await customerService?.getCustomerByUserId(user?.id);
        if (!customer) return;

        // Subscribe to order changes for this customer
        const channel = supabase?.channel('customer_orders_changes')?.on(
            'postgres_changes',
            {
              event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
              schema: 'public',
              table: 'orders',
              filter: `customer_id=eq.${customer?.id}`
            },
            (payload) => {
              console.log('Order update received:', payload);
              
              if (payload?.eventType === 'INSERT') {
                // New order created - reload all orders
                loadOrders();
              } else if (payload?.eventType === 'UPDATE') {
                // Order status updated - update the specific order
                setOrders((prevOrders) => 
                  prevOrders?.map((order) => 
                    order?.id === payload?.new?.id 
                      ? { ...order, ...payload?.new }
                      : order
                  )
                );
                
                // Show notification for status changes
                if (payload?.old?.status !== payload?.new?.status) {
                  showOrderStatusNotification(payload?.new);
                }
              } else if (payload?.eventType === 'DELETE') {
                // Order deleted - remove from list
                setOrders((prevOrders) => 
                  prevOrders?.filter((order) => order?.id !== payload?.old?.id)
                );
              }
            }
          )?.subscribe();

        // Cleanup subscription on unmount
        return () => {
          supabase?.removeChannel(channel);
        };
      } catch (err) {
        console.error('Failed to setup real-time subscription:', err);
      }
    };

    const unsubscribe = setupRealtimeSubscription();

    return () => {
      if (unsubscribe) {
        unsubscribe?.then((cleanup) => cleanup && cleanup());
      }
    };
  }, [user]);

  const loadOrders = async () => {
    setLoading(true);
    setError('');
    try {
      // Get customer profile
      const { data: customer, error: customerError } = await customerService?.getCustomerByUserId(user?.id);
      if (customerError) throw customerError;
      if (!customer) throw new Error('Customer profile not found');

      // Get all orders for this customer
      const { data: ordersData, error: ordersError } = await customerService?.getOrders(customer?.id);
      if (ordersError) throw ordersError;

      setOrders(ordersData || []);
    } catch (err) {
      console.error('Failed to load orders:', err);
      setError(err?.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const showOrderStatusNotification = (order) => {
    // Create a browser notification for order status changes
    if ('Notification' in window && Notification?.permission === 'granted') {
      const statusMessages = {
        pending: 'Order Confirmed! Your order has been received.',
        preparing: 'Order in Progress! Your meal is being prepared.',
        ready: 'Order Ready! Your order is ready for pickup/delivery.',
        delivered: 'Order Delivered! Enjoy your meal!',
        cancelled: 'Order Cancelled. Your order has been cancelled.'
      };

      new Notification('Order Update', {
        body: statusMessages?.[order?.status] || 'Your order status has been updated.',
        icon: '/favicon.ico'
      });
    }
  };

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification?.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const getFilteredOrders = () => {
    if (filter === 'all') return orders;
    return orders?.filter((order) => order?.status === filter);
  };

  const handleReorder = async (order) => {
    // Navigate to order placement with reorder data
    navigate('/order-placement', {
      state: {
        reorder: order,
        dish: order?.order_items?.[0]?.menu_item,
        supplier: order?.supplier
      }
    });
  };

  const handleViewDetails = (order) => {
    // Navigate to order details page (could be implemented later)
    console.log('View order details:', order);
  };

  const handleRateOrder = (order) => {
    // Find the full order object from orders array
    const fullOrder = orders?.find(o => o?.id === order?.id);
    setSelectedOrder(fullOrder);
    setShowRatingModal(true);
  };

  const handleTrackOrder = (order) => {
    // Navigate to order tracking page (could be implemented later)
    console.log('Track order:', order);
  };

  const formatOrderForCard = (order) => {
    const firstItem = order?.order_items?.[0];
    const menuItem = firstItem?.menu_item;
    
    return {
      id: order?.id,
      orderId: order?.order_number,
      dishName: menuItem?.name || 'Unknown Item',
      dishImage: menuItem?.image || '/assets/images/no_image.png',
      dishImageAlt: menuItem?.image_alt || 'Order item image',
      supplierName: order?.supplier?.business_name || 'Unknown Supplier',
      status: order?.status,
      orderDate: order?.created_at,
      quantity: order?.order_items?.reduce((sum, item) => sum + item?.quantity, 0) || 0,
      // Price per plate (prefer order item's explicit price or menu item's price_per_plate)
      pricePerPlate: Number(firstItem?.price_per_plate ?? firstItem?.price ?? firstItem?.menu_item?.price_per_plate ?? firstItem?.menu_item?.price ?? 0).toFixed(2),
      totalAmount: parseFloat(order?.total_amount || 0)?.toFixed(2),
      estimatedDelivery: order?.estimated_delivery
    };
  };

  const filterOptions = [
    { value: 'all', label: 'All Orders', icon: 'List' },
    { value: 'pending', label: 'Pending', icon: 'Clock' },
    { value: 'preparing', label: 'Preparing', icon: 'ChefHat' },
    { value: 'ready', label: 'Ready', icon: 'Package' },
    { value: 'delivered', label: 'Delivered', icon: 'CheckCircle2' },
    { value: 'cancelled', label: 'Cancelled', icon: 'XCircle' }
  ];

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <>
        <Helmet>
          <title>My Orders - HomeKitchen</title>
          <meta name="description" content="View and track all your orders" />
        </Helmet>
        <div className="min-h-screen bg-background">
          <RoleBasedNavigation />
          <main className="container mx-auto px-4 py-8">
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          </main>
        </div>
      </>
    );
  }

  if (!user) {
    return <AuthenticationPrompt />;
  }

  const filteredOrders = getFilteredOrders();

  return (
    <>
      <Helmet>
        <title>My Orders - HomeKitchen</title>
        <meta name="description" content="View and track all your orders" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <RoleBasedNavigation />

        <main className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-heading font-bold text-3xl md:text-4xl mb-2">
              My Orders
            </h1>
            <p className="text-muted-foreground">
              Track and manage all your orders in one place
            </p>
          </div>

          {/* Filter Tabs */}
          <div className="mb-6 overflow-x-auto">
            <div className="flex gap-2 min-w-max">
              {filterOptions?.map((option) => (
                <button
                  key={option?.value}
                  onClick={() => setFilter(option?.value)}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-smooth flex items-center gap-2 ${
                    filter === option?.value
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-card text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={option?.icon} size={16} />
                  {option?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 text-destructive">
                <Icon name="AlertCircle" size={20} />
                <p className="font-medium">{error}</p>
              </div>
            </div>
          )}

          {/* Orders List */}
          {!loading && !error && (
            <>
              {filteredOrders?.length === 0 ? (
                <div className="bg-card rounded-xl shadow-warm p-12 text-center">
                  <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="ShoppingBag" size={40} className="text-muted-foreground" />
                  </div>
                  <h3 className="font-heading font-semibold text-xl mb-2">
                    No orders found
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    {filter === 'all' ? "You haven't placed any orders yet"
                      : `No ${filter} orders found`}
                  </p>
                  <Button
                    onClick={() => navigate('/customer-dashboard')}
                    iconName="ChefHat"
                  >
                    Browse Kitchens
                  </Button>
                </div>
              ) : (
                <div className="grid gap-4">
                  {filteredOrders?.map((order) => (
                    <OrderHistoryCard
                      key={order?.id}
                      order={formatOrderForCard(order)}
                      onReorder={handleReorder}
                      onViewDetails={handleRateOrder}
                      onTrackOrder={handleTrackOrder}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </main>

        <RatingModal
          isOpen={showRatingModal}
          onClose={() => {
            setShowRatingModal(false);
            setSelectedOrder(null);
          }}
          order={selectedOrder}
          onRatingSubmitted={() => {
            setShowRatingModal(false);
            setSelectedOrder(null);
            loadOrders();
          }}
        />
      </div>
    </>
  );
};

export default MyOrders;